<template>
  <DocSummaryPage
    :doc="book"
    :isLoadingDoc="isLoadingDoc"
    :isErrorLoadingDoc="isErrorLoadingDoc"
  />
</template>

<script setup lang="ts">
import { computed, onBeforeMount, Raw, ref } from 'vue'
import { useWait } from '../composables/useWait'
import {
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
  RouteLocationNormalized,
  useRoute,
  useRouter,
} from 'vue-router'

import { backend } from '@/services/backend'

import { Sentry } from '@/services/sentry'
import { assertNotNull } from '@/helpers/typing'
import { aiLabel13537Experiment, streaks16464Experiment } from '@/services/ab'

import { BookView } from '@/models/doc.book'
import { backLink } from '@/models/backlink'

import { offline } from '@/services/offline'
import { loadBookDetailFromNativeStorage } from '@/services/native.storage'
import DocSummaryPage from '@/app/components/doc/DocSummaryPage.vue'

const route = useRoute()
const router = useRouter()
const wait = useWait()

const book = ref<Raw<BookView> | null>(null)

onBeforeRouteLeave(
  async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: any,
  ) => {
    // Called when the route that renders this component is about to
    // be navigated away from.
    // Has access to `this` component instance.
    if (book.value) {
      await book.value.updateReadTimeAndSavePosition()
      backLink.value.updateMetaTitle(from, book.value.title)
    }
    next()
  },
)

onBeforeRouteUpdate(
  async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: any,
  ) => {
    const bookValue = assertNotNull(book.value)

    bookValue.initPage(to, from, loadDocument)
    backLink.value.updateMetaTitle(from, bookValue.title)
    next()
  },
)

onBeforeMount(async () => {
  wait.start('Loading Doc')
  // Start the reading streak experiment
  await streaks16464Experiment()

  await loadDocument(route)

  if (book.value && book.value.is_ai) {
    await aiLabel13537Experiment()
  }
})

async function _fetchDocument(
  route: RouteLocationNormalized,
): Promise<BookView> {
  if (offline.isOffline) {
    await loadBookDetailFromNativeStorage(route.params.url_slug as string)
  }
  const response = await backend.getBook(route.params.url_slug as string)
  const docView = new BookView(response)
  docView.getPageFromRoute(route)
  return docView
}

async function loadDocument(route: RouteLocationNormalized): Promise<void> {
  try {
    wait.start('Loading Doc')
    wait.end('Error Loading Doc')

    book.value = await _fetchDocument(route)

    // Disabling the restoring when accessing the page from highlights page
    const restorePositionByReadingProgress =
      route.query?.highlight === undefined
    await book.value.initDocument(restorePositionByReadingProgress)

    if (!book.value.isPreviewPage) {
      await book.value.scrollToTop()
    }
  } catch (error: any) {
    if (error.request && error.request.status === 404) {
      // Redirect to the non-existing page
      router.push({ name: 'not_found' })
    } else {
      wait.start('Error Loading Doc')
      Sentry.captureException(error)
    }
  } finally {
    wait.end('Loading Doc')
  }
}

const isLoadingDoc = computed(() => {
  return wait.is('Loading Doc')
})

const isErrorLoadingDoc = computed(() => {
  return wait.is('Error Loading Doc')
})
</script>
